import { wrap, wrapGql } from "../_"
import { DeletionReason } from "@/types/api/user"

type UpdateUserData = any // TODO
type CreateUserData = any // TODO
type CreateUserQuickData = any // TODO

const pfx = "/user"

export default (a: NuxtAxiosInstance) => ({
  getFavoriteUsers: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/favorite`,
      { ...cfg })),

  addFavoriteUser: (userId: string, cfg?: AxiosConfig) => wrap(
    a.$post(`${pfx}/${userId}/favorite`, {},
      { ...cfg })),

  removeFavoriteUser: (userId: string, cfg?: AxiosConfig) => wrap(
    a.$delete(`${pfx}/${userId}/favorite`,
      { ...cfg })),

  getUsers: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}`,
      { cache: true, ...cfg })),

  getUser: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/my`,
      { ...cfg })),

  getCountries: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/countries`,
      { cache: true, ...cfg })),

  getColleague: (userId: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/colleague/${userId}`,
      { ...cfg })),

  updateColleague: (userId: string, data: UpdateUserData, cfg?: AxiosConfig) =>
    wrap(
      a.$patch(`${pfx}/colleague/${userId}`, data,
        { ...cfg })),

  createColleague: (data: CreateUserData, cfg?: AxiosConfig) => wrap(
    a.$post(`${pfx}/colleague`, data,
      { ...cfg })),

  sendToken: (variables: {
    email: string
    personalDataConsent: boolean
    captcha: string
  }) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation (
          $email: String!
          $personalDataConsent: Boolean!
          $captcha: String!
        ) {
          createRegistrationToken(
            email: $email
            personalDataConsent: $personalDataConsent
            captcha: $captcha
          )
        }
      `,
      variables,
    }),
    "createRegistrationToken",
  ),

  findRegistrationToken: (code: string) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query (
          $code: String!
        ) {
          findRegistrationToken(
            code: $code
          ) {
            id
            target
            code
          }
        }
      `,
      variables: {
        code,
      },
    }),
    "findRegistrationToken",
  ),

  getTokenData: (token: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/registration-token/${token}`,
      { ...cfg })),

  createUserQuick: (data: CreateUserQuickData, cfg?: AxiosConfig) => wrap(
    a.$post(`${pfx}/quick-registration`, data,
      { ...cfg })),

  getPreviousData: (token: string, cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/previous-user/${token}`,
      { ...cfg })),

  getA5KUsers: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/a5k`,
      { cache: true, ...cfg })),

  getRecommendedUsers: (cfg?: AxiosConfig) => wrap(
    a.$get(`${pfx}/recommended`,
      { ...cfg })),

  findUser: (id: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: `
        query ($id: UUID!) {
          findUser(id: $id) {
            id
            oauthID
            firstName {
              current
            }
            lastName {
              current
            }
          }
        }
      `,
      variables: { id },
    }, { ...cfg }),
    "findUser",
  ),

  getFormExtraValidationData: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          getFormExtraValidationData
        }
      `,
    },
    cfg),
  ),

  listOutdatedUserDocumentConsent: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql?event=main", {
      query: /* gql */ `
        query {
          listOutdatedUserDocumentConsent {
            id
            consentField {
              alias
              form {
                schema
              }
            }
          }
        }
      `,
    },
    cfg),
  ),

  startPhotoVerification: (upload: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation ($upload: UploadPath!) {
          startPhotoVerification(upload: $upload)
        }
      `,
      variables: { upload },
    }, cfg),
  ),

  getPhotoVerificationResults: (token: string, cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query ($token: String!) {
          getPhotoVerificationResults(token: $token) {
            status
            issues
          }
        }
      `,
      variables: { token },
    },
    cfg),
  ),

  createUserDeleteRequest: (variables: {
    deleteReason: DeletionReason,
    secondUser?: string,
    feedback?: string,
    otherReason?: string,
  }) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation (
          $deleteReason: UserDeleteRequestReason!
          $secondUser: UUID = null
          $feedback: String = null
          $otherReason: String = null
        ) {
          createUserDeleteRequest(
            deleteReason: $deleteReason
            secondUser: $secondUser
            feedback: $feedback
            otherReason: $otherReason
          ) {
            id
            creationTime
            updateTime
            author { id }
            secondUser { id }
            state
            deleteReason
            feedback
            otherReason
          }
        }
      `,
      variables,
    }),
    "createUserDeleteRequest",
  ),

  revokeUserDeleteRequest: () => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation {
          deleteMyUserDeleteRequest
        }
      `,
    }),
    "deleteMyUserDeleteRequest",
  ),

  findUserDeleteRequest: (cfg?: AxiosConfig) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        query {
          findMyUserDeleteRequest {
            id
            creationTime
            updateTime
            author { id }
            secondUser { id }
            state
            deleteReason
            feedback
            otherReason
          }
        }
      `,
    }, cfg),
  ),

  setUserNotificationConsent: (consent: boolean) => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation (
          $consent: Boolean!
        ) {
          setUserNotificationConsent(
            consent: $consent
          )
        }
      `,
      variables: { consent },
    }),
    "setUserNotificationConsent",
  ),

  logout: () => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation {
          logout
        }
      `,
    }),
    "logout",
  ),

  createVKIntegrationURL: () => wrapGql(
    a.$post("/graphql", {
      query: /* gql */ `
        mutation {
          createUserVKIntegrationURL
        }
      `,
    }),
    "createUserVKIntegrationURL",
  ),
})
